/**
 * Timeline API Service - Clean programmatic APIs for AI code generation
 * 
 * This service provides a clean interface for AI to manipulate timelines
 * programmatically while maintaining state consistency and undo/redo support.
 */

// Note: In a React app, you might need to install uuid with: npm install uuid
// For now using a simple UUID generator
const uuidv4 = () => {
  return 'xxxx-xxxx-4xxx-yxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

class TimelineAPI {
  constructor() {
    this.clips = [];
    this.selectedClipIds = [];
    this.history = [];
    this.historyIndex = -1;
    this.duration = 0;
    this.aspectRatio = '16:9'; // '16:9' or '9:16'
    this.listeners = new Set();
    
    // Content analysis tags for AI querying
    this.contentTags = {
      talking: [],
      action: [],
      ingredients: [],
      cooking: [],
      reaction: [],
      kitchen: [],
      outdoor: [],
      closeup: [],
      wideshot: []
    };
  }

  // Event system for UI updates
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  _notify(event, data) {
    this.listeners.forEach(listener => listener(event, data));
  }

  _saveToHistory() {
    const state = {
      clips: JSON.parse(JSON.stringify(this.clips)),
      selectedClipIds: [...this.selectedClipIds],
      duration: this.duration,
      aspectRatio: this.aspectRatio,
      contentTags: JSON.parse(JSON.stringify(this.contentTags))
    };

    // Remove any history after current index (when undoing then making new changes)
    this.history = this.history.slice(0, this.historyIndex + 1);
    this.history.push(state);
    this.historyIndex++;

    // Limit history size
    if (this.history.length > 50) {
      this.history.shift();
      this.historyIndex--;
    }
  }

  // Core Timeline APIs for AI Code Generation

  /**
   * Add a clip to the timeline at specified start time
   * @param {Object} clip - Clip object with id, name, duration, url, etc.
   * @param {number} startTime - Start time in seconds
   * @returns {string} clipId
   */
  addClip(clip, startTime = null) {
    this._saveToHistory();

    const clipId = clip.id || uuidv4();
    const newClip = {
      ...clip,
      id: clipId,
      startTime: startTime !== null ? startTime : this.duration,
      duration: clip.duration || 10,
      endTime: null, // Calculated based on startTime + duration
      selected: false,
      tags: clip.tags || [],
      waveformData: clip.waveformData || [],
      thumbnails: clip.thumbnails || []
    };

    newClip.endTime = newClip.startTime + newClip.duration;

    // Insert clip in chronological order
    const insertIndex = this.clips.findIndex(c => c.startTime > newClip.startTime);
    if (insertIndex === -1) {
      this.clips.push(newClip);
    } else {
      this.clips.splice(insertIndex, 0, newClip);
    }

    this._recalculateDuration();
    this._updateContentTags(newClip);
    this._notify('clipAdded', { clipId, clip: newClip });

    return clipId;
  }

  /**
   * Move a clip to a new time position
   * @param {string} clipId - ID of clip to move
   * @param {number} newTime - New start time in seconds
   * @returns {boolean} success
   */
  moveClip(clipId, newTime) {
    this._saveToHistory();

    const clipIndex = this.clips.findIndex(c => c.id === clipId);
    if (clipIndex === -1) return false;

    const clip = this.clips[clipIndex];
    clip.startTime = Math.max(0, newTime);
    clip.endTime = clip.startTime + clip.duration;

    // Re-sort clips by start time
    this.clips.sort((a, b) => a.startTime - b.startTime);

    this._recalculateDuration();
    this._notify('clipMoved', { clipId, newTime });

    return true;
  }

  /**
   * Remove a clip from timeline
   * @param {string} clipId - ID of clip to remove
   * @returns {boolean} success
   */
  removeClip(clipId) {
    this._saveToHistory();

    const clipIndex = this.clips.findIndex(c => c.id === clipId);
    if (clipIndex === -1) return false;

    const removedClip = this.clips.splice(clipIndex, 1)[0];
    this.selectedClipIds = this.selectedClipIds.filter(id => id !== clipId);

    this._recalculateDuration();
    this._removeFromContentTags(removedClip);
    this._notify('clipRemoved', { clipId, clip: removedClip });

    return true;
  }

  /**
   * Get all clips in timeline
   * @returns {Array} clips
   */
  getClips() {
    return [...this.clips];
  }

  /**
   * Clear entire timeline
   */
  clear() {
    this._saveToHistory();

    this.clips = [];
    this.selectedClipIds = [];
    this.duration = 0;
    this.contentTags = {
      talking: [], action: [], ingredients: [], cooking: [], reaction: [],
      kitchen: [], outdoor: [], closeup: [], wideshot: []
    };

    this._notify('timelineCleared', {});
  }

  /**
   * Get total timeline duration
   * @returns {number} duration in seconds
   */
  getDuration() {
    return this.duration;
  }

  /**
   * Select clips by IDs (supports multi-select)
   * @param {Array} clipIds - Array of clip IDs to select
   */
  selectClips(clipIds) {
    this.selectedClipIds = [...clipIds];
    
    // Update clip selection state
    this.clips.forEach(clip => {
      clip.selected = clipIds.includes(clip.id);
    });

    this._notify('clipsSelected', { clipIds });
  }

  /**
   * Modify selected clips with a property/value
   * @param {string} property - Property to modify
   * @param {any} value - New value
   */
  modifySelected(property, value) {
    if (this.selectedClipIds.length === 0) return;

    this._saveToHistory();

    this.selectedClipIds.forEach(clipId => {
      const clip = this.clips.find(c => c.id === clipId);
      if (clip && clip.hasOwnProperty(property)) {
        clip[property] = value;
      }
    });

    this._recalculateDuration();
    this._notify('selectedClipsModified', { property, value, clipIds: this.selectedClipIds });
  }

  /**
   * Find clips by content tag
   * @param {string} tagName - Tag to search for
   * @returns {Array} clips with specified tag
   */
  findClipsByTag(tagName) {
    if (!this.contentTags[tagName]) return [];
    
    return this.clips.filter(clip => 
      this.contentTags[tagName].includes(clip.id)
    );
  }

  /**
   * Split clip at specified time
   * @param {string} clipId - ID of clip to split
   * @param {number} splitTime - Time within clip to split (relative to clip start)
   * @returns {Array} [leftClipId, rightClipId]
   */
  splitClip(clipId, splitTime) {
    this._saveToHistory();

    const clipIndex = this.clips.findIndex(c => c.id === clipId);
    if (clipIndex === -1) return null;

    const originalClip = this.clips[clipIndex];
    
    // Validate split time
    if (splitTime <= 0 || splitTime >= originalClip.duration) return null;

    // Create two new clips
    const leftClip = {
      ...originalClip,
      id: uuidv4(),
      duration: splitTime,
      endTime: originalClip.startTime + splitTime,
      name: `${originalClip.name} (Part 1)`
    };

    const rightClip = {
      ...originalClip,
      id: uuidv4(),
      startTime: originalClip.startTime + splitTime,
      duration: originalClip.duration - splitTime,
      endTime: originalClip.endTime,
      name: `${originalClip.name} (Part 2)`
    };

    // Replace original clip with split clips
    this.clips.splice(clipIndex, 1, leftClip, rightClip);

    this._updateContentTags(leftClip);
    this._updateContentTags(rightClip);
    this._removeFromContentTags(originalClip);

    this._notify('clipSplit', { 
      originalClipId: clipId, 
      leftClipId: leftClip.id, 
      rightClipId: rightClip.id 
    });

    return [leftClip.id, rightClip.id];
  }

  /**
   * Trim clip by adjusting start/end times
   * @param {string} clipId - ID of clip to trim
   * @param {number} newStart - New start time (relative to timeline)
   * @param {number} newEnd - New end time (relative to timeline)
   */
  trimClip(clipId, newStart, newEnd) {
    this._saveToHistory();

    const clip = this.clips.find(c => c.id === clipId);
    if (!clip) return false;

    const originalStart = clip.startTime;
    const originalEnd = clip.endTime;

    clip.startTime = Math.max(0, newStart);
    clip.endTime = Math.max(clip.startTime + 0.1, newEnd); // Minimum 0.1s duration
    clip.duration = clip.endTime - clip.startTime;

    this._recalculateDuration();
    this._notify('clipTrimmed', { 
      clipId, 
      originalStart, 
      originalEnd, 
      newStart: clip.startTime, 
      newEnd: clip.endTime 
    });

    return true;
  }

  /**
   * Set aspect ratio for timeline
   * @param {string} ratio - '16:9' or '9:16'
   */
  setAspectRatio(ratio) {
    if (!['16:9', '9:16'].includes(ratio)) return false;
    
    this._saveToHistory();
    this.aspectRatio = ratio;
    this._notify('aspectRatioChanged', { ratio });
    return true;
  }

  /**
   * Get current aspect ratio
   * @returns {string} current aspect ratio
   */
  getAspectRatio() {
    return this.aspectRatio;
  }

  // Undo/Redo functionality
  undo() {
    if (this.historyIndex <= 0) return false;

    this.historyIndex--;
    const state = this.history[this.historyIndex];
    this._restoreState(state);
    this._notify('undoRedo', { action: 'undo' });
    return true;
  }

  redo() {
    if (this.historyIndex >= this.history.length - 1) return false;

    this.historyIndex++;
    const state = this.history[this.historyIndex];
    this._restoreState(state);
    this._notify('undoRedo', { action: 'redo' });
    return true;
  }

  canUndo() {
    return this.historyIndex > 0;
  }

  canRedo() {
    return this.historyIndex < this.history.length - 1;
  }

  // Private helper methods
  _restoreState(state) {
    this.clips = JSON.parse(JSON.stringify(state.clips));
    this.selectedClipIds = [...state.selectedClipIds];
    this.duration = state.duration;
    this.aspectRatio = state.aspectRatio;
    this.contentTags = JSON.parse(JSON.stringify(state.contentTags));
  }

  _recalculateDuration() {
    if (this.clips.length === 0) {
      this.duration = 0;
      return;
    }

    this.duration = Math.max(...this.clips.map(clip => clip.endTime));
  }

  _updateContentTags(clip) {
    if (!clip.tags) return;

    clip.tags.forEach(tag => {
      if (this.contentTags[tag] && !this.contentTags[tag].includes(clip.id)) {
        this.contentTags[tag].push(clip.id);
      }
    });
  }

  _removeFromContentTags(clip) {
    Object.keys(this.contentTags).forEach(tag => {
      this.contentTags[tag] = this.contentTags[tag].filter(id => id !== clip.id);
    });
  }

  // Advanced AI-friendly methods
  
  /**
   * Get clips within time range
   * @param {number} startTime - Start time in seconds
   * @param {number} endTime - End time in seconds
   * @returns {Array} clips within range
   */
  getClipsInRange(startTime, endTime) {
    return this.clips.filter(clip => 
      clip.startTime < endTime && clip.endTime > startTime
    );
  }

  /**
   * Get timeline composition at specific time
   * @param {number} time - Time in seconds
   * @returns {Object} active clips and their properties
   */
  getCompositionAtTime(time) {
    const activeClips = this.clips.filter(clip => 
      time >= clip.startTime && time < clip.endTime
    );

    return {
      time,
      clips: activeClips,
      aspectRatio: this.aspectRatio
    };
  }

  /**
   * Analyze timeline for gaps and overlaps
   * @returns {Object} analysis results
   */
  analyzeTimeline() {
    const gaps = [];
    const overlaps = [];

    for (let i = 0; i < this.clips.length - 1; i++) {
      const currentClip = this.clips[i];
      const nextClip = this.clips[i + 1];

      if (currentClip.endTime < nextClip.startTime) {
        // Gap detected
        gaps.push({
          start: currentClip.endTime,
          end: nextClip.startTime,
          duration: nextClip.startTime - currentClip.endTime
        });
      } else if (currentClip.endTime > nextClip.startTime) {
        // Overlap detected
        overlaps.push({
          start: nextClip.startTime,
          end: currentClip.endTime,
          duration: currentClip.endTime - nextClip.startTime,
          clips: [currentClip.id, nextClip.id]
        });
      }
    }

    return { gaps, overlaps, totalGapTime: gaps.reduce((sum, gap) => sum + gap.duration, 0) };
  }
}

// Create singleton instance
const timelineAPI = new TimelineAPI();

// Export both the class and singleton for different use cases
export { TimelineAPI };
export default timelineAPI;